import {useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {CardComponent, PageHeader, TabsComponent} from '@/components/molecules'
import {FileUploadWithModal, UploadTemplateWithModal} from '@/components/organisms'
import WarehouseList from './WarehouseList/WarehouseList'
import InventoryList from './InventoryList/InventoryList'
import {downloadCSVFiles, downloadInventoryCSVFiles} from '@/helpers/shadcn/utils'
import {Constants} from '@/utils/constants/commonConstants'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useQueryClient} from '@tanstack/react-query'

/**
 * Generates a filename with current timestamp for inventory template
 * @returns {string} Formatted filename with timestamp
 */
const getInventoryTemplateFilename = () => {
  const date = new Date()
  const timestamp = date.toISOString().replace(/[-:]/g, '').replace('T', '_').split('.')[0]
  return `Inventory Template_${timestamp}`
}

/**
 * Fulfillment component that manages warehouse and inventory management interface.
 * Includes card-based navigation and tabbed content display.
 *
 * @returns {JSX.Element} The rendered fulfillment page component.
 */
export const Fulfillment = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const tab = params.get('tab') ? parseInt(params.get('tab')) : 0
  const [activeTabIndex, setActiveTabIndex] = useState(tab)
  const queryClient = useQueryClient()

  const [openUpload, setOpenUpload] = useState(false)
  const [formData, setFormData] = useState({
    targetMediaFile: [],
  })

  const inventoryApiRegion = (() => {
    try {
      return new URL(import.meta.env.VITE_INVENTORY_API_URL).pathname.split('/')[1]
    } catch {
      return ''
    }
  })()
  const csvHeaders = ['Product ID', 'Location ID', 'Parent ID', 'GTIN', 'DOH', 'WHS OOS']
  if (inventoryApiRegion === 'latam') {
    csvHeaders.push('Minimum Qty')
  }
  const csvData = csvHeaders.join(',')

  /**
   * Card config used at the top of the page
   */
  const cardList = [
    // {
    //   centralIcon: 'local_police',
    //   ctaPrimary: {
    //     text: 'Add Template',
    //     onClick: () => console.log('add template'),
    //   },
    //   title: 'Warehouses',
    // },
    {
      centralIcon: 'local_police',
      ctaPrimary: {
        text: 'Add Template',
        onClick: () => setOpenUpload(true),
      },
      title: 'Inventory',
    },
  ]

  /**
   * Tab config for toggling between "Warehouses" and "Inventory"
   */
  const tabList = [
    // {
    //   id: 'Warehouses',
    //   tabLabel: 'Warehouses',
    //   title: 'Warehouses',
    //   tabComponent: <WarehouseList />,
    // },
    {
      id: 'Inventory',
      tabLabel: 'Inventory',
      title: 'Inventory',
      tabComponent: <InventoryList />,
    },
  ]

  const handleUploadSuccess = async () => {
    // Invalidate the inventory queries to trigger a refetch
    await queryClient.invalidateQueries(['INVENTORY'])
    await queryClient.invalidateQueries(['INVENTORY_UPLOAD_HISTORY'])
    setActiveTabIndex(0)
  }

  return (
    <div className="fullfillmentContainer flex flex-col">
      <PageHeader title="Fullfillment" />
      <div className="targetCardComponent mb-5">
        <CardComponent cardList={cardList} />
      </div>
      <TabsComponent tabList={tabList} defaultTabIndex={activeTabIndex} onTabChange={setActiveTabIndex} />
      {/* <UploadTemplateWithModal openUpload={openUpload} setOpenUpload={setOpenUpload} /> */}
      <FileUploadWithModal
        openUpload={openUpload}
        setOpenUpload={setOpenUpload}
        setFormData={setFormData}
        formData={formData}
        title="Upload Inventory Template"
        showTertiaryButton={true}
        uploadFor={'inventory'}
        isCsvValidationRequired={true}
        isTemplateUpload={true}
        required={false}
        label={
          <span>
            {Constants.uploadInventory}{' '}
            <span
              tabIndex={0}
              role="button"
              onClick={() => downloadInventoryCSVFiles(csvData, getInventoryTemplateFilename())}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  downloadInventoryCSVFiles(csvData, getInventoryTemplateFilename())
                }
              }}
              style={{textDecoration: 'underline', color: '#007bff', cursor: 'pointer'}}
            >
              here
            </span>
            .
          </span>
        }
        toastMessage={'File has successfully been uploaded and the status can be checked in Upload History'}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  )
}
